import { Box, Stack, styled, Theme, Typography, useMediaQuery, useTheme } from '@mui/material';

import { useRouter } from 'next/router';
import { ComponentType, FC, PropsWithChildren, useState } from 'react';

import { HBButton } from '../../HBButton/HBButton';
import { HBCarousel } from '../../HBCarousel/HBCarousel';
import { HBCountDownTimer } from '../../HBCountDownTimer/HBCountDownTimer';
import { HBIcon } from '../../HBIcon/HBIcon';
import { HBLink } from '../../HBLink/HBLink';
import { HBCardWidget as CardWidgetDefault } from './HBCardWidget';
import { CardType, CollectionData, HBCardWidgetProps } from './HBCardWidgetType';

export const HorizontalScrollerWithNoScroll = styled(Box)<{ spacing?: number }>(() => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}));

export interface ICardWidgetListProps {
  type: CardType;
  content: {
    metaData?: string;
    data?: CollectionData[];
    title?: string;
    description?: string;
    isSliderOrAd?: boolean | null;
  };
  endDate?: string | null;
  noDivider?: boolean;
  addToBasketTitle?: HBCardWidgetProps['addToBasketTitle'];
  unavailableTitle?: HBCardWidgetProps['unavailableTitle'];
  cardWidgetCustom?: ComponentType<HBCardWidgetProps>;
  prefixImageUrl: string;
  noAction?: boolean;
  slidesPerView?: number;
  data?: CollectionData[] | null;
  unAvailableProductId?: string;
  name?: string | null;
  collectionOriginName?: string | null;
}

export enum WidgetLinkEnum {
  inFrame = '1113002',
  newTab = '1113001',
}

export const HBCardWidgetList: FC<PropsWithChildren<ICardWidgetListProps>> = ({
  content,
  type,
  noDivider,
  addToBasketTitle,
  unavailableTitle,
  cardWidgetCustom: CardWidgetCustom,
  prefixImageUrl,
  noAction,
  slidesPerView,
  data,
  endDate,
  unAvailableProductId,
  name,
  collectionOriginName,
}) => {
  const theme = useTheme();
  const router = useRouter();
  const [hideWidget, setHideWidget] = useState(false);
  const metaData = content?.metaData ? JSON.parse(content?.metaData) : {};
  const breakpointsMdDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const breakpointsSmDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const CardWidget = CardWidgetCustom ?? CardWidgetDefault;

  const sidebar = (
    <Stack
      sx={{
        bgcolor: metaData?.backgroundColor,
        borderTopLeftRadius: { lg: 16 },
        borderBottomLeftRadius: { lg: 16 },
        width: { xs: 150, md: 200 },
        px: { xs: 2, md: 4 },
        height: { xs: '100%', md: 'unset' },
      }}
      justifyContent="center"
      alignItems="center"
      spacing={10}
    >
      <Stack spacing={4}>
        <Typography
          sx={{ textAlign: 'center', color: metaData?.fontColor }}
          variant="h5"
          textAlign="center"
          component="div"
        >
          {content?.title}
        </Typography>
        <Typography
          component="div"
          dangerouslySetInnerHTML={{ __html: content?.description + '' }}
          textAlign="center"
        />
      </Stack>
      {metaData?.hasButton && (
        <HBLink
          href={
            noAction
              ? undefined
              : metaData?.button.link ||
                '/collection/' + (collectionOriginName || `?collectionId=${metaData?.queryId}`)
          }
          target={
            metaData.button.webLinkNavigationType === WidgetLinkEnum.inFrame ? '_self' : '_blank'
          }
          passHref
        >
          <HBButton
            sx={{
              borderRadius: 6,
              bgcolor: metaData?.button?.backgroundColor,
              color: `${metaData?.button?.fontColor} !important`,
              display: 'flex',
              alignItems: 'flex-start',
              width: 'max-content',
            }}
          >
            <Typography variant="button" color={metaData?.button?.fontColor}>
              {metaData?.button.text}
            </Typography>
            <HBIcon type="arrowLeft" />
          </HBButton>
        </HBLink>
      )}
      {endDate && (
        <Box>
          <HBCountDownTimer
            targetDate={new Date(endDate)}
            maximumShowed="hours"
            linkText=""
            sx={{
              '& h5': (theme) => ({
                color: 'common.white',
                pt: 2,
                pb: 1,
                borderRadius: 2,
                backgroundColor: 'transparent',
                boxShadow: 'inset 0px 2px 9px -6px rgb(255 255 255 / 94%)',
                minWidth: 32,
                ...theme.typography.subtitle1,
              }),
              '& p': {
                color: 'common.white',
              },
            }}
            onFinished={() => setHideWidget(true)}
          />
        </Box>
      )}
    </Stack>
  );

  const handleLink = (url: string, target: string, queryId: string) => {
    if (router.pathname.startsWith('/vendor/')) {
      const toStore = router.query['toStore'];
      router.replace(
        router.basePath + router.asPath + (toStore ? '' : '&toStore=true'),
        undefined,
        {
          shallow: true,
        },
      );
    } else if (url) window.open(url, target === WidgetLinkEnum.inFrame ? '_self' : '_blank');
    else router.push('/collection/' + (collectionOriginName || `?collectionId=${queryId}`));
  };

  if (!data?.length || hideWidget) return null;

  return (
    <Stack
      className={name || ''}
      spacing={7}
      sx={{
        bgcolor:
          type === 'BUSINESS_SLIDER_DEFAULT' && !!metaData?.backgroundColor
            ? metaData.backgroundColor
            : 'common.white',
        py: type !== 'BUSINESS_SLIDER_WITH_SIDEBAR' ? 6 : 'unset',
        pl: type !== 'BUSINESS_SLIDER_WITH_SIDEBAR' ? 6 : 'unset',
        borderRadius:
          type === 'BUSINESS_SLIDER_DEFAULT' || type === 'BUSINESS_SLIDER_WITH_SIDEBAR'
            ? { xs: 'unset', lg: 4 }
            : 4,
        pr: 0,
        overflow: 'hidden',
        width: (theme) => ({
          xs: `calc(100% + ${theme.spacing(6)})`,
          md: `calc(100% + ${theme.spacing(8)})`,
          lg: 'unset',
        }),
        marginLeft: { xs: -3, md: -4, lg: 'unset' },
      }}
    >
      {type !== 'BUSINESS_SLIDER_WITH_SIDEBAR' && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent={{ xs: 'space-between', md: 'unset' }}
          spacing={6}
          height={32}
        >
          {content.isSliderOrAd && (
            <>
              <Typography variant={'h6'} color={metaData?.fontColor} component="h3">
                {content?.title}
              </Typography>
              {!!metaData?.hasButton && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  bgcolor={metaData?.button?.backgroundColor || 'unset'}
                  pl={4}
                  pr={{ xs: 4, sm: 2.5 }}
                  py={0.88}
                  borderRadius={2}
                  sx={{ cursor: 'pointer' }}
                  onClick={() =>
                    noAction
                      ? undefined
                      : handleLink(
                          metaData?.button?.link,
                          metaData?.button?.webLinkNavigationType,
                          metaData.queryId,
                        )
                  }
                  data-test-id="card-widget-list-link"
                >
                  <Typography variant="button" color={metaData?.button?.fontColor}>
                    {metaData?.button?.text}
                  </Typography>
                  {!breakpointsSmDown && (
                    <HBIcon type="angleLeft" sx={{ color: metaData?.button?.fontColor }} />
                  )}
                </Stack>
              )}
            </>
          )}
        </Stack>
      )}

      <Box
        sx={{
          border: (theme) =>
            type === 'BUSINESS_SLIDER_WITH_SIDEBAR'
              ? `1px solid ${theme.palette.grey[100]}`
              : 'unset',
          borderRadius: type === 'BUSINESS_SLIDER_WITH_SIDEBAR' && breakpointsMdDown ? 0 : 2,
          display: 'flex',
          '& div.swipper': {
            ...(type === 'BUSINESS_SLIDER_WITH_SIDEBAR'
              ? {
                  '& .swiper-button-prev, & .swiper-button-next': {
                    opacity: 0,
                    '&:hover': {
                      opacity: '1 !important',
                    },
                  },
                  '&:hover': {
                    '& .swiper-button-prev': {
                      opacity: 0.7,
                    },
                    '& .swiper-button-next': {
                      opacity: 0.7,
                    },
                  },
                }
              : {
                  overflow: 'unset',
                  '& .swiper-button-prev': {
                    top: -43,
                    right: 64,
                    left: 'unset',
                  },
                  '& .swiper-button-next': {
                    top: -43,
                  },
                  '& .swiper-button-disabled': {
                    display: 'flex',
                  },
                }),
          },

          '& div.swiper-wrapper': {
            '&:first-of-type': {
              pl: { md: type === 'BUSINESS_SLIDER_WITH_SIDEBAR' ? 6 : 0 },
            },
            '& > div.swiper-slide:last-child': {
              paddingRight: { xs: 1, md: 4 },
            },
          },
        }}
      >
        {type === 'BUSINESS_SLIDER_WITH_SIDEBAR' && !breakpointsMdDown && sidebar}

        <HBCarousel
          freeMode={true}
          spaceBetween={24}
          slidesPerView={slidesPerView || 'auto'}
          delay={
            type === 'BUSINESS_SLIDER_WITH_SIDEBAR' && breakpointsMdDown
              ? undefined
              : metaData?.delayTime || undefined
          }
          style={
            type === 'BUSINESS_SLIDER_WITH_SIDEBAR'
              ? {
                  padding: theme.spacing(breakpointsMdDown ? 0 : 6),
                  paddingLeft: 'unset',
                  paddingRight: '0',
                  width: breakpointsMdDown ? 'unset' : 'calc(100% - 200px)',
                  overflow: 'hidden',
                }
              : {}
          }
          navigation={!breakpointsMdDown}
        >
          {type === 'BUSINESS_SLIDER_WITH_SIDEBAR' && breakpointsMdDown && sidebar}

          {data?.map((item, index) => (
            <Box
              key={item.id}
              height="100%"
              py={type === 'BUSINESS_SLIDER_WITH_SIDEBAR' && breakpointsMdDown ? 6 : 'unset'}
            >
              <CardWidget
                data={{ ...item, imageUrl: prefixImageUrl + item.imageUrl }}
                type={type}
                key={item.id! + index}
                noDivider={noDivider}
                addToBasketTitle={addToBasketTitle}
                unavailableTitle={unavailableTitle}
                noAction={noAction}
                unAvailableProductId={unAvailableProductId}
              />
            </Box>
          ))}
        </HBCarousel>
      </Box>
    </Stack>
  );
};
